import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { BookOpen, Menu, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Markdown } from './Markdown';
import { getDoc, manualDocs, manualSections, searchManual } from './manual';

export default function HelpPage() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const slug = params['*'] || '';
  const doc = getDoc(slug || undefined);

  const [query, setQuery] = useState('');
  const [isContentsOpen, setIsContentsOpen] = useState(false);

  const results = useMemo(() => searchManual(query), [query]);
  const isSearching = query.trim().length >= 2;

  // A #fragment jumps to its heading; App's ScrollToTop handles plain page changes.
  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [slug, location.hash]);

  // Navigating closes the mobile contents panel.
  useEffect(() => {
    setIsContentsOpen(false);
  }, [slug]);

  const firstDoc = manualDocs[0];

  // An unknown slug (or landing on /help) falls back to the first page.
  if (!doc || !slug) {
    return firstDoc ? <Navigate to={`/help/${firstDoc.slug}`} replace /> : null;
  }

  const sidebar = (
    <div className="space-y-6">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the manual"
          aria-label="Search the manual"
          className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-transparent text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      {isSearching ? (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">
            {results.length === 0
              ? 'No matches'
              : `${results.length} ${results.length === 1 ? 'page' : 'pages'}`}
          </p>
          <ul className="space-y-2">
            {results.map((hit) => (
              <li key={hit.doc.slug}>
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    navigate(`/help/${hit.doc.slug}`);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
                >
                  <span className="block text-sm font-medium text-gray-900">{hit.doc.title}</span>
                  <span className="block text-xs text-gray-500 mt-0.5 line-clamp-2">
                    {hit.snippet}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        manualSections.map((section) => (
          <div key={section.key}>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-3">
              {section.label}
            </p>
            <ul className="space-y-0.5">
              {section.docs.map((entry) => {
                const isActive = entry.slug === doc.slug;
                return (
                  <li key={entry.slug}>
                    <Link
                      to={`/help/${entry.slug}`}
                      className={cn(
                        'block px-3 py-2 rounded-md text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      )}
                    >
                      {entry.title}
                    </Link>

                    {/* The active page expands to its own section headings. */}
                    {isActive && entry.headings.length > 0 && (
                      <ul className="mt-1 mb-2 ml-3 border-l border-gray-200 space-y-0.5">
                        {entry.headings
                          .filter((heading) => heading.level === 2)
                          .map((heading) => (
                            <li key={heading.id}>
                              <Link
                                to={`/help/${entry.slug}#${heading.id}`}
                                className="block pl-3 pr-2 py-1 text-xs text-gray-500 hover:text-gray-900 transition-colors"
                              >
                                {heading.text}
                              </Link>
                            </li>
                          ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* A label, not a heading — each manual page supplies the page's own h1. */}
      <div className="flex items-center gap-2 mb-6 text-gray-500">
        <BookOpen className="h-5 w-5" />
        <span className="text-sm font-semibold uppercase tracking-wide">User Manual</span>
      </div>

      {/* Mobile: the sidebar collapses behind a Contents toggle. */}
      <button
        type="button"
        onClick={() => setIsContentsOpen((open) => !open)}
        className="md:hidden flex items-center gap-2 mb-4 px-3 py-2 rounded-md border border-gray-200 text-sm font-medium text-gray-700"
        aria-expanded={isContentsOpen}
      >
        {isContentsOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        Contents
      </button>

      <div className="flex gap-8">
        <nav className="hidden md:block w-64 flex-shrink-0" aria-label="Manual contents">
          <div className="sticky top-24">{sidebar}</div>
        </nav>

        {isContentsOpen && (
          <nav className="md:hidden w-full mb-6" aria-label="Manual contents">
            {sidebar}
          </nav>
        )}

        {!isContentsOpen && (
          <main className="flex-1 min-w-0">
            <article className="bg-white rounded-lg border border-gray-200 px-6 py-8 sm:px-10">
              <Markdown baseDir={doc.section}>{doc.body}</Markdown>
            </article>
          </main>
        )}
      </div>
    </div>
  );
}
